import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  LogOut, RefreshCw, Users, CalendarCheck, Building2, ShieldCheck, Send, Bell, BellOff,
} from 'lucide-react';

import { API, authHeaders } from './admin/constants';
import { readSession, writeSession, clearSession } from './admin/session';
import { StatCard } from './admin/shared';
import Login from './admin/Login';
import AppointmentsTab from './admin/AppointmentsTab';
import LeadsTab from './admin/LeadsTab';
import BusinessHoursTab from './admin/BusinessHoursTab';
import BlockedDatesTab from './admin/BlockedDatesTab';
import HistoryDrawer from './admin/HistoryDrawer';
import WaitlistTab from './admin/WaitlistTab';
import LaunchComposerModal from './admin/LaunchComposerModal';
import PatientFormsTab from './admin/PatientFormsTab';

const TABS = [
  { key: 'appointments', label: 'APPOINTMENTS' },
  { key: 'leads', label: 'CORPORATE LEADS' },
  { key: 'waitlist', label: 'WAITLIST' },
  { key: 'forms', label: 'PATIENT FORMS' },
  { key: 'hours', label: 'BUSINESS HOURS' },
  { key: 'blocked', label: 'BLOCKED DATES' },
];

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState('appointments');
  const [appts, setAppts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [hours, setHours] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [patientForms, setPatientForms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newBlock, setNewBlock] = useState({ date: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [hoursMsg, setHoursMsg] = useState('');
  const [digestMsg, setDigestMsg] = useState('');
  const [notifOn, setNotifOn] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [launchTarget, setLaunchTarget] = useState(null); // {service, waitingCount}
  const [launchMessage, setLaunchMessage] = useState('');
  const lastSeenLatestId = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [a, l, s, b, rec, hrs, wl, pf] = await Promise.all([
        axios.get(`${API}/appointments`, authHeaders(token)),
        axios.get(`${API}/business-leads`, authHeaders(token)),
        axios.get(`${API}/admin/stats`, authHeaders(token)),
        axios.get(`${API}/admin/blocked-dates`, authHeaders(token)),
        axios.get(`${API}/admin/recurring-blocks`, authHeaders(token)),
        axios.get(`${API}/admin/business-hours`, authHeaders(token)),
        axios.get(`${API}/admin/waitlist`, authHeaders(token)),
        axios.get(`${API}/admin/patient-forms`, authHeaders(token)),
      ]);
      setAppts(a.data);
      setLeads(l.data);
      setStats(s.data);
      setBlocked(b.data);
      setRecurring(rec.data);
      setHours(hrs.data);
      setWaitlist(wl.data);
      setPatientForms(pf.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        setError('Could not load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  // Poll for new bookings every 20s and fire a browser notification when the latest changes.
  useEffect(() => {
    if (!token) return undefined;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/new-count`, authHeaders(token));
        if (
          lastSeenLatestId.current &&
          data.latest_appointment_id &&
          data.latest_appointment_id !== lastSeenLatestId.current
        ) {
          if (notifOn && 'Notification' in window && Notification.permission === 'granted') {
            new Notification('New Aurelia booking', {
              body: `${data.new_appointments} awaiting confirmation`,
              icon: '/generated/medical_case.png',
            });
          }
          load();
        }
        lastSeenLatestId.current = data.latest_appointment_id || lastSeenLatestId.current;
      } catch (err) {
        console.error('New-count poll failed:', err);
      }
    };
    tick();
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [token, notifOn, load]);

  const updateAppt = async (id, status) => {
    setAppts((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { status }, authHeaders(token));
    } catch (err) {
      console.error('Failed to update appointment status:', err);
      load();
    }
  };

  const updateLead = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await axios.patch(`${API}/business-leads/${id}/status`, { status }, authHeaders(token));
    } catch (err) {
      console.error('Failed to update lead status:', err);
      load();
    }
  };

  const addBlocked = async (e) => {
    e.preventDefault();
    if (!newBlock.date) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/blocked-dates`, newBlock, authHeaders(token));
      setBlocked((prev) => [...prev, data].sort((x, y) => x.date.localeCompare(y.date)));
      setNewBlock({ date: '', reason: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not block date.');
    } finally {
      setBusy(false);
    }
  };

  const removeBlocked = async (id) => {
    try {
      await axios.delete(`${API}/admin/blocked-dates/${id}`, authHeaders(token));
      setBlocked((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error('Failed to remove blocked date:', err);
      load();
    }
  };

  const toggleRecurring = async (weekday, reason = 'Standing day off') => {
    const existing = recurring.find((r) => r.weekday === weekday);
    if (existing) {
      try {
        await axios.delete(`${API}/admin/recurring-blocks/${existing.id}`, authHeaders(token));
        setRecurring((prev) => prev.filter((r) => r.id !== existing.id));
      } catch (err) {
        console.error('Failed to remove recurring block:', err);
        load();
      }
    } else {
      try {
        const { data } = await axios.post(
          `${API}/admin/recurring-blocks`,
          { weekday, reason },
          authHeaders(token),
        );
        setRecurring((prev) => [...prev, data].sort((a, b) => a.weekday - b.weekday));
      } catch (err) {
        alert(err.response?.data?.detail || 'Could not update recurring block.');
      }
    }
  };

  const updateHour = (weekday, field, value) => {
    setHours((prev) => prev.map((h) => (h.weekday === weekday ? { ...h, [field]: value } : h)));
  };

  const saveHours = async () => {
    setSavingHours(true);
    setHoursMsg('');
    try {
      const { data } = await axios.put(`${API}/admin/business-hours`, { hours }, authHeaders(token));
      setHours(data);
      setHoursMsg('Business hours saved.');
    } catch (err) {
      setHoursMsg(err.response?.data?.detail || 'Could not save hours.');
    } finally {
      setSavingHours(false);
      setTimeout(() => setHoursMsg(''), 4000);
    }
  };

  const sendDigest = async () => {
    setDigestMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/send-weekly-digest`, {}, authHeaders(token));
      setDigestMsg(data.sent ? 'Digest sent to admin inbox.' : 'Email not sent — check SMTP settings.');
    } catch {
      setDigestMsg('Failed to send digest.');
    }
    setTimeout(() => setDigestMsg(''), 5000);
  };

  const openHistory = async (email) => {
    try {
      const { data } = await axios.get(`${API}/admin/client-history`, {
        ...authHeaders(token),
        params: { email },
      });
      setHistoryClient(data);
    } catch {
      setHistoryClient({ email, count: 0, appointments: [] });
    }
  };

  const saveNotes = async (id) => {
    const notes = notesDraft[id] ?? '';
    try {
      await axios.patch(`${API}/appointments/${id}/notes`, { notes }, authHeaders(token));
      setAppts((prev) => prev.map((a) => (a.id === id ? { ...a, notes } : a)));
      if (historyClient) {
        setHistoryClient((h) => h && ({
          ...h,
          appointments: h.appointments.map((a) => (a.id === id ? { ...a, notes } : a)),
        }));
      }
      setNotesDraft((d) => { const n = { ...d }; delete n[id]; return n; });
    } catch (err) {
      console.error('Failed to save appointment notes:', err);
    }
  };

  const enableNotifications = async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') { setNotifOn(true); return; }
    const perm = await Notification.requestPermission();
    setNotifOn(perm === 'granted');
  };

  const updateWaitlistStatus = async (id, status) => {
    setWaitlist((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
    try {
      await axios.patch(`${API}/admin/waitlist/${id}/status`, { status }, authHeaders(token));
    } catch (err) {
      console.error('Failed to update waitlist status:', err);
      load();
    }
  };

  const deleteWaitlistEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, authHeaders(token));
      setWaitlist((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      console.error('Failed to delete waitlist entry:', err);
      load();
    }
  };

  const deletePatientForm = async (id) => {
    if (!window.confirm('Remove this signed consent form? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/patient-forms/${id}`, authHeaders(token));
      setPatientForms((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      console.error('Failed to delete consent form:', err);
      load();
    }
  };

  const openLaunch = (service, waitingCount) => {
    setLaunchMessage('');
    setLaunchTarget({ service, waitingCount });
  };

  const sendLaunch = async ({ subject, message }) => {
    if (!launchTarget) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/waitlist/launch`,
        { service: launchTarget.service, subject, message },
        authHeaders(token),
      );
      const failedNote = data.failed ? `, ${data.failed} failed` : '';
      setLaunchMessage(
        data.sent
          ? `Sent ${data.sent} launch email${data.sent === 1 ? '' : 's'} for ${data.service}${failedNote}.`
          : `No emails were sent for ${data.service} — check SMTP settings.`,
      );
      setLaunchTarget(null);
      load();
    } catch (err) {
      setLaunchMessage(err.response?.data?.detail || 'Failed to send launch announcement.');
      setLaunchTarget(null);
    }
    setTimeout(() => setLaunchMessage(''), 8000);
  };

  const totalAppts = useMemo(() => stats?.appointments?.total ?? appts.length, [stats, appts]);
  const newAppts = useMemo(
    () => stats?.appointments?.new ?? appts.filter((a) => a.status === 'new').length,
    [stats, appts],
  );
  const totalLeads = useMemo(() => stats?.business_leads?.total ?? leads.length, [stats, leads]);
  const newLeads = useMemo(
    () => stats?.business_leads?.new ?? leads.filter((l) => l.status === 'new').length,
    [stats, leads],
  );

  return (
    <div className="min-h-[calc(100vh-160px)] bg-[#faf6f0] py-12 px-6" data-testid="admin-dashboard">
      <div className="max-w-[1240px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold mb-2">CONCIERGE OPERATIONS</p>
            <h1
              className="font-serif text-4xl text-[#1c1e20]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Bookings Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={enableNotifications}
              data-testid="toggle-notifications"
              className={`flex items-center gap-2 border px-4 py-2 text-[11px] tracking-[0.2em] font-semibold transition-colors ${
                notifOn ? 'border-[#c9a961] bg-[#c9a961] text-white' : 'border-black/15 hover:border-[#c9a961] text-[#1c1e20]'
              }`}
              title="Get a browser notification when new bookings arrive"
            >
              {notifOn ? <Bell size={14} /> : <BellOff size={14} />}{' '}
              {notifOn ? 'NOTIFICATIONS ON' : 'ENABLE ALERTS'}
            </button>
            <button
              onClick={load}
              data-testid="refresh-button"
              className="flex items-center gap-2 border border-black/15 hover:border-[#c9a961] text-[#1c1e20] px-4 py-2 text-[11px] tracking-[0.2em] font-semibold"
            >
              <RefreshCw size={14} /> REFRESH
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 bg-[#141618] hover:bg-black text-white px-4 py-2 text-[11px] tracking-[0.2em] font-semibold"
            >
              <LogOut size={14} /> SIGN OUT
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
          <StatCard Icon={CalendarCheck} label="APPOINTMENTS" value={totalAppts} sub="all-time" />
          <StatCard Icon={Users} label="NEW BOOKINGS" value={newAppts} sub="awaiting confirmation" />
          <StatCard Icon={Building2} label="CORPORATE LEADS" value={totalLeads} sub="all-time" />
          <StatCard Icon={ShieldCheck} label="OPEN LEADS" value={newLeads} sub="not yet actioned" />
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-black/10 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-5 py-3 text-[11px] tracking-[0.24em] font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.key ? 'text-[#1c1e20] border-[#c9a961]' : 'text-[#6b6b6b] border-transparent hover:text-[#1c1e20]'
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3 pb-2">
            {digestMsg && <span className="text-[12px] text-[#6b6b6b]">{digestMsg}</span>}
            <button
              onClick={sendDigest}
              data-testid="send-weekly-digest"
              className="flex items-center gap-2 border border-[#c9a961] text-[#8a7132] hover:bg-[#c9a961] hover:text-white transition-colors px-4 py-2 text-[11px] tracking-[0.2em] font-semibold"
            >
              <Send size={13} /> SEND WEEKLY DIGEST
            </button>
          </div>
        </div>

        {error && <p className="text-rose-600 text-sm mb-4" data-testid="admin-error">{error}</p>}
        {loading && <p className="text-[#6b6b6b] text-sm" data-testid="admin-loading">Loading…</p>}

        {!loading && tab === 'appointments' && (
          <AppointmentsTab appts={appts} onUpdateStatus={updateAppt} onOpenHistory={openHistory} />
        )}
        {!loading && tab === 'leads' && (
          <LeadsTab leads={leads} onUpdateStatus={updateLead} />
        )}
        {!loading && tab === 'waitlist' && (
          <WaitlistTab
            entries={waitlist}
            onUpdateStatus={updateWaitlistStatus}
            onDelete={deleteWaitlistEntry}
            onOpenLaunch={openLaunch}
            launchMessage={launchMessage}
          />
        )}
        {!loading && tab === 'forms' && (
          <PatientFormsTab forms={patientForms} onDelete={deletePatientForm} />
        )}
        {!loading && tab === 'hours' && (
          <BusinessHoursTab
            hours={hours}
            onUpdateHour={updateHour}
            onSave={saveHours}
            saving={savingHours}
            message={hoursMsg}
          />
        )}
        {!loading && tab === 'blocked' && (
          <BlockedDatesTab
            recurring={recurring}
            blocked={blocked}
            newBlock={newBlock}
            busy={busy}
            onToggleRecurring={toggleRecurring}
            onNewBlockChange={setNewBlock}
            onAddBlocked={addBlocked}
            onRemoveBlocked={removeBlocked}
          />
        )}
      </div>

      <HistoryDrawer
        client={historyClient}
        notesDraft={notesDraft}
        onNotesDraftChange={(id, v) => setNotesDraft((d) => ({ ...d, [id]: v }))}
        onSaveNotes={saveNotes}
        onClose={() => setHistoryClient(null)}
      />

      {launchTarget && (
        <LaunchComposerModal
          service={launchTarget.service}
          waitingCount={launchTarget.waitingCount}
          onCancel={() => setLaunchTarget(null)}
          onSend={sendLaunch}
        />
      )}
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => readSession());
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios
      .get(`${API}/admin/verify`, authHeaders(token))
      .then(() => setChecking(false))
      .catch(() => {
        clearSession();
        setToken('');
        setChecking(false);
      });
  }, [token]);

  const handleLogin = (t) => {
    writeSession(t);
    setToken(t);
  };

  const logout = () => {
    clearSession();
    setToken('');
  };

  if (checking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#6b6b6b]" data-testid="admin-verifying">
        Verifying…
      </div>
    );
  }
  if (!token) return <Login onLogin={handleLogin} />;
  return <Dashboard token={token} onLogout={logout} />;
};

export default Admin;
